import {
  INTERNAL_FEATURE_CATEGORY, isUsableReadiness, type FeatureSummary, type PlanSummary,
} from './types';

/**
 * Pure helpers behind the plan comparison and the feature badges. They only arrange what `GET /api/subscription-plans` and
 * `GET /api/subscription-features` returned: no price, plan name, feature or limit is invented here, and nothing decides what a
 * company may do (the server does that on every request).
 */

/** What a feature looks like to a customer right now, independent of any plan. */
export type FeatureAvailability = 'available' | 'beta' | 'coming-soon' | 'pilot-only' | 'unavailable';

export function featureAvailability(feature: Pick<FeatureSummary, 'readinessStatus'>): FeatureAvailability {
  switch (feature.readinessStatus) {
    case 'Operational': return 'available';
    case 'Beta': return 'beta';
    case 'PilotOnly': return 'pilot-only';
    case 'AdministrativelyDisabled': return 'unavailable';
    // ComingSoon, NotImplemented and any value a newer server adds: never advertised as available.
    default: return 'coming-soon';
  }
}

/** The badge text for a feature, or null when it is simply available and needs no label. */
export function availabilityLabel(availability: FeatureAvailability): string | null {
  switch (availability) {
    case 'beta': return 'Beta';
    case 'coming-soon': return 'Coming soon';
    case 'pilot-only': return 'Pilot only';
    case 'unavailable': return 'Not available right now';
    default: return null;
  }
}

/** A feature a company on a plan that includes it can use today (Operational or Beta). */
export function isUsableFeature(feature: FeatureSummary): boolean {
  return isUsableReadiness(feature.readinessStatus);
}

/** The catalog minus the internal demo rows ("Platform Examples"), which no customer-facing comparison may show. */
export function customerFacingFeatures(features: readonly FeatureSummary[]): FeatureSummary[] {
  return features.filter((f) => f.category !== INTERNAL_FEATURE_CATEGORY);
}

export interface FeatureGroup {
  category: string;
  features: FeatureSummary[];
}

/** Groups by category in the order the API listed them (the server already orders by category and display order). */
export function groupFeaturesByCategory(features: readonly FeatureSummary[]): FeatureGroup[] {
  const groups: FeatureGroup[] = [];
  for (const feature of features) {
    const existing = groups.find((g) => g.category === feature.category);
    if (existing) existing.features.push(feature);
    else groups.push({ category: feature.category, features: [feature] });
  }
  return groups;
}

/** "Core Payroll" becomes "Core": the short form used in "Everything in Core". Falls back to the whole name. */
export function shortPlanName(name: string): string {
  const short = name.replace(/\s+payroll$/i, '').trim();
  return short.length > 0 ? short : name;
}

export function sortPlans(plans: readonly PlanSummary[]): PlanSummary[] {
  return plans.slice().sort((a, b) => a.tier - b.tier);
}

/** The plan one tier below, or null for the lowest plan. */
export function planBelow(plans: readonly PlanSummary[], plan: PlanSummary): PlanSummary | null {
  const lower = sortPlans(plans).filter((p) => p.tier < plan.tier);
  return lower[lower.length - 1] ?? null;
}

/** The plan one tier above `tier`, or null when `tier` is already the highest. Used for "what the next plan adds". */
export function planAboveTier(plans: readonly PlanSummary[], tier: number): PlanSummary | null {
  return sortPlans(plans).find((p) => p.tier > tier) ?? null;
}

/** "Everything in Core" for Plus, "Everything in Plus" for Pro Plus, null for the lowest plan. */
export function inheritanceLabel(plans: readonly PlanSummary[], plan: PlanSummary): string | null {
  const below = planBelow(plans, plan);
  return below ? `Everything in ${shortPlanName(below.name)}` : null;
}

/** The features whose required plan is exactly this one: what the plan ADDS to the plan below it. Everything lower is inherited. */
export function featuresAddedBy(features: readonly FeatureSummary[], plan: PlanSummary): FeatureSummary[] {
  return features.filter((f) => f.minimumPlanTier === plan.tier);
}

/** Whether a plan's tier reaches the feature's required plan. A feature no plan includes (`minimumPlanTier` null) is in no plan. */
export function planIncludes(plan: Pick<PlanSummary, 'tier'>, feature: FeatureSummary): boolean {
  return feature.minimumPlanTier !== null && plan.tier >= feature.minimumPlanTier;
}
